<template>
    <div class="l-padded l-stack l-center chart-container">
        <div
            v-if="isLoading || isLoadingData"
            class="l-full-height l-stack l-center"
        >
            <VSpinner size="medium" line-fg-color="#000" :speed="1" />
        </div>

        <div v-else-if="series[0].data.length" class="chart-container__content">
            <ApexCharts
                ref="chart"
                height="180"
                :options="chartOptions"
                :series="series"
                @scrolled="handleZoom"
                @zoomed="handleZoom"
                @click="handleDatapointSelection"
            />

            <div>
                <VSlider
                    v-model="sliderValue"
                    drag-on-click
                    :duration="0"
                    included
                    :marks="sliderValues"
                    :min="timeStart"
                    :max="timeEnd"
                    :tooltip-formatter="sliderTooltipFormatter"
                    :tooltip="sliderValue ? 'active' : 'none'"
                    @change="handleSliderValueChange"
                />
            </div>

            <p
                v-if="sliderValues.length > 1"
                class="l-inline l-gap-2 l-center-v l-spread"
            >
                <span>{{ timeStartFormatted }}</span>

                <span class="l-inline l-gap-2">
                    <IconButton
                        no-button
                        :hover-color="iconButtonHoverColor"
                        @click="handlePlayingAccelerationChange"
                    >
                        {{ playingAcceleration }}X
                    </IconButton>

                    <IconButton
                        :hover-color="iconButtonHoverColor"
                        @click="isPlaying = !isPlaying"
                    >
                        <ControlsPauseIcon v-if="isPlaying" />

                        <ControlsPlayIcon v-else />
                    </IconButton>

                    <IconButton
                        :hover-color="iconButtonHoverColor"
                        @click="isPlayingLoop = !isPlayingLoop"
                    >
                        <LoopIcon :color="isPlayingLoop ? '#000' : '#777'" />
                    </IconButton>
                </span>

                <span>
                    {{ timeEndFormatted }}
                </span>
            </p>
        </div>

        <p v-else>
            {{ $t('noResults') }}
        </p>
    </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { debounce, throttle } from 'lodash'
import moment from 'moment-timezone'
import ApexCharts from 'vue-apexcharts'
import VSlider from 'vue-slider-component'
import VSpinner from 'vue-simple-spinner'

import { httpHelper, measurementHelper } from '@/utils'
import ChartHelper from '@/mixins/ChartHelper'
import ControlsPauseIcon from './icons/ControlsPauseIcon'
import ControlsPlayIcon from './icons/ControlsPlayIcon'
import IconButton from './IconButton'
import LoopIcon from './icons/LoopIcon'

export default {
    name: 'AssetLocationHistoryMeasurementsChart',
    components: {
        ApexCharts,
        ControlsPauseIcon,
        ControlsPlayIcon,
        IconButton,
        LoopIcon,
        VSlider,
        VSpinner,
    },
    mixins: [ChartHelper],
    props: {
        historyPoints: {
            type: Array,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        id: {
            type: [String, Number],
            required: true,
        },
        measurement: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            chartOptions: {
                chart: {
                    type: 'line',
                    stacked: false,
                    animations: {
                        enabled: false,
                    },
                    ...(this.measurement === 'accel_y' && {
                        type: 'scatter',
                    }),
                    events: {
                        mouseMove: this.handleChartMouseMove,
                    },
                },
                grid: {
                    show: false,
                },
                stroke: {
                    curve: 'straight',
                    width: 3,
                },
                colors: [process.env.VUE_APP_COLOR_PRIMARY],
                dataLabels: {
                    enabled: false,
                },
                markers: {
                    size: 0,
                    style: 'full',
                    ...(this.measurement === 'accel_y' && {
                        size: 8,
                    }),
                },
                yaxis: {
                    min: this.getYMinValue(this.measurement),
                    max: this.getYMaxValue(this.measurement),
                    floating: true,
                    tickAmount: 2,
                    labels: {
                        offsetY: -4,
                        offsetX: -2,
                        maxWidth: 100,
                        align: 'left',
                    },
                    ...(['bin', 'sbb-bin'].includes(this.assetType) &&
                        this.measurement === 'distance' && {
                            min: 0,
                            max: 100,
                        }),
                    ...(this.measurement === 'accel_y' && {
                        show: false,
                        tickAmount: 2,
                        min: 0,
                        max: 2,
                    }),
                },
                xaxis: {
                    type: 'datetime',
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: false,
                    },
                    labels: {
                        show: false,
                    },
                    tooltip: {
                        enabled: false,
                    },
                },
                tooltip: {
                    shared: false,
                    x: {
                        format: 'dd.MM.yy HH:mm:ss',
                    },
                    ...(this.measurement === 'accel_y' && {
                        custom: () => null,
                    }),
                },
            },
            assetHistoryPointLocked: false,
            iconButtonHoverColor: 'rgba(0, 0, 0, 0.05)',
            isLoadingData: false,
            isPlaying: false,
            isPlayingLoop: false,
            playingAcceleration: 1,
            playingAccelerationOptions: [0.5, 1, 1.5, 2],
            playingIndex: null,
            playingIntervalId: null,
            series: [{ data: [] }],
            sliderValue: null,
        }
    },
    computed: {
        ...mapState('tracker', [
            'assetHistory',
            'assetHistoryLocationId',
            'assetHistoryPointProposed',
        ]),
        converter() {
            if (this.measurement === 'distance') {
                if (this.assetType === 'bin') {
                    return measurementHelper.convertToBinLevel
                } else if (this.assetType === 'sbb-bin') {
                    return measurementHelper.convertToSbbBinLevel
                }
            }

            return (
                measurementHelper.converters[this.measurement] ||
                measurementHelper.converters.default
            )
        },
        historyPointsByDate() {
            return this.historyPoints.reduce((acc, point) => {
                acc[point.timestamp] = point
                return acc
            }, {})
        },
        measurementTranslated() {
            return this.$root.$te(`shared.measurements.${this.measurement}`)
                ? this.$t(`shared.measurements.${this.measurement}`)
                : this.measurement
        },
        sliderValues() {
            return [
                ...new Set(
                    this.historyPoints.map(item =>
                        new Date(item.timestamp).getTime()
                    )
                ),
            ].sort((a, b) => a - b)
        },
        timeEnd() {
            return this.sliderValues[this.sliderValues.length - 1]
        },
        timeEndFormatted() {
            return this.sliderTooltipFormatter(this.timeEnd)
        },
        timeStart() {
            return this.sliderValues[0]
        },
        timeStartFormatted() {
            return this.sliderTooltipFormatter(this.timeStart)
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                const { playbackspeed } = this.$route.query
                if (playbackspeed) {
                    const parsed = JSON.parse(playbackspeed)
                    if (parsed?.every(Number.isFinite)) {
                        this.playingAccelerationOptions = parsed
                    }
                    this.$router.replace({
                        query: {
                            ...this.$route.query,
                            playbackspeed: undefined,
                        },
                    })
                }
            },
        },
        assetHistoryLocationId() {
            if (this.assetHistoryLocationId) {
                const timestamp = this.assetHistory.find(
                    item => item.id === this.assetHistoryLocationId
                )?.timestamp
                this.sliderValue = timestamp
                    ? new Date(timestamp).getTime()
                    : null
            }
        },
        assetHistoryPointProposed() {
            if (
                this.assetHistoryPointProposed &&
                !this.assetHistoryPointLocked
            ) {
                const timestamp = this.historyPoints.find(
                    ({ position: [lat, lng] }) =>
                        lat === this.assetHistoryPointProposed[0] &&
                        lng === this.assetHistoryPointProposed[1]
                )?.timestamp
                this.sliderValue = timestamp
                    ? new Date(timestamp).getTime()
                    : null
                this.setAssetHistoryPointProposed(null)
            }
        },
        historyPoints() {
            this.loadHistory()
        },
        id() {
            this.loadHistory()
        },
        isPlaying() {
            if (!this.isPlaying) {
                clearInterval(this.playingIntervalId)
                return
            }

            this.$refs.chart.chart.resetSeries()
            this.playingIndex = this.sliderValues.indexOf(this.sliderValue)
            this.playingIntervalId = setInterval(() => {
                if (this.playingIndex === this.sliderValues.length - 1) {
                    if (this.isPlayingLoop) {
                        this.playingIndex = -1
                    } else {
                        this.isPlaying = false
                        this.sliderValue = null
                        this.$emit('pointChange', null)
                        return
                    }
                }
                this.sliderValue = this.sliderValues[++this.playingIndex]
                this.$emit('pointChange', this.historyPoints[this.playingIndex])
            }, 12 / this.playingAcceleration)
        },
        measurement() {
            this.updateYAxis()
            this.loadHistory()
        },
        playingAcceleration() {
            if (this.isPlaying) {
                this.isPlaying = false
                this.$nextTick(() => {
                    this.isPlaying = true
                })
            }
        },
    },
    mounted() {
        this.loadHistory()
    },
    beforeDestroy() {
        if (this.playingIntervalId) {
            clearInterval(this.playingIntervalId)
        }
    },
    methods: {
        ...mapMutations('tracker', ['setAssetHistoryPointProposed']),
        updateYAxis() {
            this.chartOptions.yaxis.min = this.getYMinValue(this.measurement)
            this.chartOptions.yaxis.max = this.getYMaxValue(this.measurement)
        },
        toggleAssetHistoryPointLocked() {
            this.assetHistoryPointLocked = !this.assetHistoryPointLocked
        },
        async loadHistory() {
            this.isLoadingData = true
            const results = await this.loadData()
            this.series = [
                {
                    name: this.measurementTranslated,
                    data: results
                        .filter(item =>
                            Object.prototype.hasOwnProperty.call(
                                item.sensor_data,
                                this.measurement
                            )
                        )
                        .map(item => [
                            item.timestamp,
                            this.converter(item.sensor_data[this.measurement]),
                        ]),
                },
            ]
            this.isLoadingData = false
        },
        async loadData() {
            if (!this.historyPoints.length) {
                return []
            }

            const { data } = await httpHelper.getRecursively('/measurements/', {
                params: {
                    fields: 'timestamp,sensor_data',
                    limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
                    timestamp_max: this.historyPoints[
                        this.historyPoints.length - 1
                    ].timestamp,
                    timestamp_min: this.historyPoints[0].timestamp,
                    tracker: this.id,
                },
            })

            return data.results
        },
        handlePlayingAccelerationChange() {
            let index = this.playingAccelerationOptions.indexOf(
                this.playingAcceleration
            )
            this.playingAcceleration = this.playingAccelerationOptions[
                ++index % this.playingAccelerationOptions.length
            ]
        },
        handleChartMouseMove(event, chartContext, opts) {
            if (this.assetHistoryPointLocked) {
                return
            }
            const dataPoint = this.series[0].data[opts.dataPointIndex]

            if (dataPoint) {
                this.$emit(
                    'pointChange',
                    this.historyPointsByDate[dataPoint[0]]
                )
            }
        },
        handleDatapointSelection(event, chartContext, opts) {
            this.toggleAssetHistoryPointLocked()
            if (!this.assetHistoryPointLocked) {
                return chartContext.removeAnnotation('selected-point')
            }
            const dataPoint = this.series[0].data[opts.dataPointIndex]
            if (!dataPoint) {
                return
            }
            chartContext.addPointAnnotation({
                id: 'selected-point',
                x: new Date(dataPoint[0]).getTime(),
                y: dataPoint[1],
                label: {
                    offsetY: 0,
                    text: dataPoint[1],
                },
            })
        },
        handleSliderValueChange: throttle(function(time) {
            this.$refs.chart.chart.resetSeries()

            this.$emit(
                'pointChange',
                this.historyPoints.find(
                    item => new Date(item.timestamp).getTime() === time
                )
            )

            if (this.isPlaying) {
                this.playingIndex = this.sliderValues.indexOf(this.sliderValue)
            }
        }, 50),
        handleZoom: debounce(function(_, { xaxis: { min, max } }) {
            const bounds = this.historyPoints
                .filter(item => {
                    const time = new Date(item.timestamp).getTime()
                    return (!min || time >= min) && (!max || time <= max)
                })
                .map(item => item.position)
            this.$emit('boundsChange', bounds)
        }, 100),
        sliderTooltipFormatter(timestamp) {
            return timestamp
                ? moment(timestamp).format('DD.MM.YYYY HH:mm:ss')
                : null
        },
    },
}
</script>

<i18n>
{
    "en": {
        "noResults": "No results"
    },
    "de": {
        "noResults": "Keine Ergebnisse"
    },
    "fr": {
        "noResults": "Aucun résultat"
    },
    "it": {
        "noResults": "Nessun Risultato"
    }
}
</i18n>

<style lang="scss" scoped>
.chart-container {
    min-height: 263px;
    text-align: center;

    &__content {
        width: 100%;

        & > * {
            &:not(:first-child) {
                padding-right: 10px;
                padding-left: 12px;
                font-size: 13px;
            }
        }

        .icon-button {
            min-width: 18px;
            height: 18px;
            line-height: 18px;
        }
    }
}
</style>
